import ListPagination from '../components/common/ListPagination';
import useListPagination from '../hooks/useListPagination';
import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  HomeIcon,
  PlusIcon,
  PencilIcon,
  TrashIcon,
  CheckCircleIcon,
  XCircleIcon,
  XMarkIcon,
  MagnifyingGlassIcon,
  CalendarIcon,
  UsersIcon,
  ChevronDownIcon,
  ChevronUpIcon
} from '@heroicons/react/24/outline';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Dropdown from '../components/ui/Dropdown';
import MultiSelect from '../components/ui/MultiSelect';
import EmployeeAvatar from '../components/common/EmployeeAvatar';
import { useAuth } from '../contexts/AuthContext';
import EmployeeService from '../services/employee.service';
import WFHService from '../services/wfh.service';
import toast from '../utils/toast';
import { formatDateDisplay } from '../utils/dateDisplay';

const resolveEmployeeFromRequest = (req, employees) => {
  const employeeEmail = req.email_address || req.employeeEmail || '';
  const employeeRecord =
    req.employee ||
    employees.find(
      (emp) =>
        String(emp.email_address).toLowerCase() === String(employeeEmail).toLowerCase()
    );
  const employeeName =
    (req.employee
      ? `${req.employee.first_name || ''} ${req.employee.last_name || ''}`.trim()
      : '') ||
    req.employeeName ||
    (employeeRecord
      ? `${employeeRecord.first_name || ''} ${employeeRecord.last_name || ''}`.trim()
      : '') ||
    'Employee';
  return { employeeEmail, employeeRecord, employeeName };
};

const WFHManagement = () => {
  const { isAdmin, isHR } = useAuth();
  const [listItems, setListItems] = useState([]);
  const [flatRequests, setFlatRequests] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [loading, setLoading] = useState(true);

  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [editingBatch, setEditingBatch] = useState(null);
  const [form, setForm] = useState({
    employeeEmail: '',
    employeeEmails: [],
    date: '',
    reason: '',
    description: ''
  });
  const [submitting, setSubmitting] = useState(false);

  const [showRejectModal, setShowRejectModal] = useState(false);
  const [selected, setSelected] = useState(null);
  const [rejectionReason, setRejectionReason] = useState('');

  const [statusFilter, setStatusFilter] = useState('');
  const [searchTerm, setSearchTerm] = useState('');
  const [expandedGroups, setExpandedGroups] = useState({});

  useEffect(() => {
    if (isAdmin || isHR) {
      fetchRequests();
      fetchEmployees();
    }
  }, [isAdmin, isHR]);

  const fetchRequests = async () => {
    try {
      setLoading(true);
      const data = await WFHService.getAllRequests();
      const items = Array.isArray(data.items)
        ? data.items
        : Array.isArray(data)
        ? data.map((req) => ({ type: 'single', id: String(req.id), items: [req] }))
        : [];
      const flat = Array.isArray(data.requests)
        ? data.requests
        : items.flatMap((entry) => entry.items || []);
      setListItems(items);
      setFlatRequests(flat);
    } catch (error) {
      console.error('Error fetching WFH requests:', error);
      toast.error('Failed to load WFH requests');
      setListItems([]);
      setFlatRequests([]);
    } finally {
      setLoading(false);
    }
  };

  const fetchEmployees = async () => {
    try {
      const response = await EmployeeService.getAll();
      const list = Array.isArray(response)
        ? response
        : Array.isArray(response?.data)
        ? response.data
        : [];
      setEmployees(list);
    } catch (error) {
      console.error('Error fetching employees:', error);
      setEmployees([]);
    }
  };

  const resetForm = () => {
    setForm({
      employeeEmail: '',
      employeeEmails: [],
      date: '',
      reason: '',
      description: ''
    });
    setEditing(null);
    setEditingBatch(null);
  };

  const employeeList = Array.isArray(employees) ? employees : [];
  const employeeOptions = employeeList.map((emp) => ({
    value: emp.email_address,
    label: `${emp.first_name} ${emp.last_name} (${emp.empcode})`
  }));
  const allEmployeesSelected =
    employeeList.length > 0 && form.employeeEmails.length === employeeList.length;

  const toggleSelectAllEmployees = () => {
    setForm((p) => ({
      ...p,
      employeeEmails: allEmployeesSelected
        ? []
        : employeeList.map((e) => e.email_address)
    }));
  };

  const openAdd = () => {
    resetForm();
    setShowForm(true);
  };

  const openEdit = (req) => {
    setEditingBatch(null);
    setEditing(req);
    setForm({
      employeeEmail: req.email_address || req.employeeEmail || '',
      employeeEmails: [],
      date: req.date ? String(req.date).split('T')[0] : '',
      reason: req.reason || '',
      description: req.description || ''
    });
    setShowForm(true);
  };

  const openEditBatch = (entry) => {
    setEditing(null);
    setEditingBatch(entry);
    const dateSource = entry.date || entry.items?.[0]?.date;
    const reasonSource = entry.reason || entry.items?.[0]?.reason;
    setForm({
      employeeEmail: '',
      employeeEmails: (entry.items || [])
        .map((req) => req.email_address || req.employeeEmail)
        .filter(Boolean),
      date: dateSource ? String(dateSource).split('T')[0] : '',
      reason: reasonSource || '',
      description: ''
    });
    setShowForm(true);
  };

  const closeForm = () => {
    setShowForm(false);
    resetForm();
  };

  const validate = () => {
    if (editingBatch) {
      if (!form.employeeEmails?.length) {
        toast.error('Please select at least one employee');
        return false;
      }
    } else if (editing) {
      if (!form.employeeEmail) {
        toast.error('Please select an employee');
        return false;
      }
    } else if (!form.employeeEmails?.length) {
      toast.error('Please select at least one employee');
      return false;
    }
    if (!form.date) {
      toast.error('Please choose a WFH date');
      return false;
    }
    if (!form.reason.trim()) {
      toast.error('Please enter a reason');
      return false;
    }
    return true;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const reason = form.reason.trim();
      if (editingBatch) {
        const items = editingBatch.items || [];
        const oldByEmail = new Map(
          items.map((req) => [
            String(req.email_address || req.employeeEmail).toLowerCase(),
            req
          ])
        );
        const newEmails = form.employeeEmails.map((e) => String(e).trim()).filter(Boolean);
        const newEmailSet = new Set(newEmails.map((e) => e.toLowerCase()));

        const results = await Promise.allSettled([
          ...items
            .filter((req) =>
              newEmailSet.has(
                String(req.email_address || req.employeeEmail).toLowerCase()
              )
            )
            .map((req) =>
              WFHService.updateRequestDetails(req.id, {
                employeeEmail: req.email_address || req.employeeEmail,
                date: form.date,
                reason
              })
            ),
          ...items
            .filter(
              (req) =>
                !newEmailSet.has(
                  String(req.email_address || req.employeeEmail).toLowerCase()
                )
            )
            .map((req) => WFHService.deleteRequest(req.id)),
          ...newEmails
            .filter((email) => !oldByEmail.has(email.toLowerCase()))
            .map((employeeEmail) =>
              WFHService.declareWFH({ employeeEmail, date: form.date, reason })
            )
        ]);
        const failed = results.filter((r) => r.status === 'rejected').length;
        if (failed > 0) {
          toast.error(`Batch update completed with ${failed} error(s)`);
        } else {
          toast.success('WFH batch updated successfully');
        }
        closeForm();
        fetchRequests();
      } else if (editing) {
        await WFHService.updateRequestDetails(editing.id, {
          employeeEmail: form.employeeEmail,
          date: form.date,
          reason
        });
        toast.success('WFH request updated successfully');
        closeForm();
        fetchRequests();
      } else if (form.employeeEmails.length === 1) {
        await WFHService.declareWFH({
          employeeEmail: form.employeeEmails[0],
          date: form.date,
          reason
        });
        toast.success('WFH declared for employee successfully');
        closeForm();
        fetchRequests();
      } else {
        const response = await WFHService.declareWFHBatch({
          employeeEmails: form.employeeEmails,
          date: form.date,
          reason
        });
        const count = response?.declaredCount ?? form.employeeEmails.length;
        toast.success(`WFH declared for ${count} employees (saved as one batch)`);
        closeForm();
        fetchRequests();
      }
    } catch (error) {
      console.error('Error saving WFH:', error);
      const msg = error.message || error.response?.data?.message || 'Failed to save WFH request';
      toast.error(msg);
    } finally {
      setSubmitting(false);
    }
  };

  const handleApproval = async (id, status, reason = '') => {
    try {
      const payload = { status };
      if (status === 'rejected' && reason) payload.rejectionReason = reason;
      await WFHService.updateRequestStatus(id, payload);
      toast.success(`WFH request ${status} successfully`);
      fetchRequests();
    } catch (error) {
      console.error('Error updating WFH request:', error);
      const msg = error.message || error.response?.data?.message || 'Failed to update WFH request';
      toast.error(msg);
    }
  };

  const handleGroupApproval = async (groupItems, status, reason = '') => {
    try {
      const results = await Promise.allSettled(
        groupItems.map((req) => {
          const payload = { status };
          if (status === 'rejected' && reason) payload.rejectionReason = reason;
          return WFHService.updateRequestStatus(req.id, payload);
        })
      );
      const succeeded = results.filter((r) => r.status === 'fulfilled').length;
      const failed = results.length - succeeded;
      if (failed === 0) {
        toast.success(
          `WFH request${groupItems.length > 1 ? 's' : ''} ${status} for ${succeeded} employee${
            succeeded > 1 ? 's' : ''
          }`
        );
      } else if (succeeded > 0) {
        toast.error(`Updated ${succeeded} of ${groupItems.length} requests`);
      } else {
        toast.error('Failed to update WFH requests');
      }
      fetchRequests();
    } catch (error) {
      console.error('Error updating WFH group:', error);
      toast.error(error.message || error.response?.data?.message || 'Failed to update WFH requests');
    }
  };

  const handleBatchDelete = async (entry) => {
    const count = entry.items?.length ?? 0;
    if (
      !window.confirm(
        `Delete WFH batch for ${count} employees on this date? This cannot be undone.`
      )
    ) {
      return;
    }
    try {
      if (entry.batchId) {
        await WFHService.deleteBatch(entry.batchId);
      } else {
        await Promise.allSettled(
          entry.items.map((req) => WFHService.deleteRequest(req.id))
        );
      }
      toast.success(`Deleted WFH batch (${count} employees)`);
      fetchRequests();
    } catch (error) {
      console.error('Error deleting WFH batch:', error);
      toast.error(error.message || error.response?.data?.message || 'Failed to delete WFH batch');
    }
  };

  const toggleGroupExpanded = (groupId) => {
    setExpandedGroups((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
  };

  const confirmReject = async () => {
    if (!rejectionReason.trim()) {
      toast.error('Please provide a reason for rejection');
      return;
    }
    if (selected?.groupItems?.length) {
      await handleGroupApproval(selected.groupItems, 'rejected', rejectionReason);
    } else {
      await handleApproval(selected.id, 'rejected', rejectionReason);
    }
    setShowRejectModal(false);
    setRejectionReason('');
    setSelected(null);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this WFH request?')) return;
    try {
      await WFHService.deleteRequest(id);
      toast.success('WFH request deleted successfully');
      fetchRequests();
    } catch (error) {
      console.error('Error deleting WFH:', error);
      const msg = error.message || error.response?.data?.message || 'Failed to delete WFH request';
      toast.error(msg);
    }
  };

  const getStatusColor = (status) => {
    const s = (status || '').toLowerCase();
    if (s === 'approved') return 'text-green-700 bg-green-100';
    if (s === 'rejected') return 'text-red-700 bg-red-100';
    return 'text-yellow-700 bg-yellow-100';
  };

  const entryMatchesFilters = (entry) => {
    const q = searchTerm.trim().toLowerCase();
    const status = (entry.status || entry.items?.[0]?.status || '').toLowerCase();
    const matchesStatus = !statusFilter || status === statusFilter.toLowerCase();
    if (!matchesStatus) return false;
    if (!q) return true;
    const reason = (entry.reason || entry.items?.[0]?.reason || '').toLowerCase();
    if (reason.includes(q)) return true;
    return (entry.items || []).some((req) => {
      const { employeeName, employeeEmail } = resolveEmployeeFromRequest(req, employees);
      return (
        employeeName.toLowerCase().includes(q) || employeeEmail.toLowerCase().includes(q)
      );
    });
  };

  const filteredEntries = listItems.filter(entryMatchesFilters);
  const pagination = useListPagination(filteredEntries, JSON.stringify([searchTerm, statusFilter]));

  if (!isAdmin && !isHR) {
    return (
      <div className="flex items-center justify-center min-h-96">
        <Card>
          <Card.Body className="p-12 text-center">
            <HomeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Access Denied</h3>
            <p className="text-gray-600">Only Admins and HRs can manage WFH requests.</p>
          </Card.Body>
        </Card>
      </div>
    );
  }



  return (
    <div className="space-y-6">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="bg-gradient-to-r from-emerald-600 via-green-600 to-teal-600 rounded-xl p-6 text-white relative overflow-hidden"
      >
        <div className="absolute inset-0 bg-black opacity-10"></div>
        <div className="relative z-10 flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold mb-2">Work From Home Management</h1>
            <p className="text-green-100">Review, approve and declare employee WFH requests</p>
          </div>
          <div className="text-right">
            <div className="text-2xl font-bold">{flatRequests.length}</div>
            <div className="text-sm text-green-200">Total Requests</div>
          </div>
        </div>
      </motion.div>

      <Card>
        <Card.Body className="p-6">
          <div className="flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex flex-1 flex-col gap-4 sm:flex-row sm:items-center">
              <div className="relative flex-1 min-w-0 sm:max-w-md">
                <MagnifyingGlassIcon className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                <input
                  type="text"
                  placeholder="Search by name, email or reason..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                />
              </div>
              <Dropdown
                value={statusFilter}
                onChange={setStatusFilter}
                options={[
                  { value: '', label: 'All Statuses' },
                  { value: 'pending', label: 'Pending' },
                  { value: 'approved', label: 'Approved' },
                  { value: 'rejected', label: 'Rejected' }
                ]}
                placeholder="All Statuses"
                className="w-full sm:w-[200px]"
              />
            </div>
            <Button onClick={openAdd} icon={PlusIcon}>
              Declare WFH for Employee
            </Button>
          </div>
        </Card.Body>
      </Card>

      <div ref={pagination.topRef}><ListPagination {...pagination} compact /></div>
      {loading ? (
        <div className="space-y-4">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="h-28 bg-gray-200 animate-pulse rounded-xl"></div>
          ))}
        </div>
      ) : filteredEntries.length === 0 ? (
        <Card>
          <Card.Body className="p-12 text-center">
            <HomeIcon className="w-16 h-16 text-gray-400 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">No WFH requests found</h3>
            <p className="text-gray-600">
              {searchTerm || statusFilter
                ? 'Try adjusting your filters.'
                : 'There are no WFH requests yet.'}
            </p>
          </Card.Body>
        </Card>
      ) : (
        <div className="space-y-4">
          {pagination.items.map((entry, index) => {
            const isGroup = entry.type === 'batch';
            const primaryReq = entry.items[0];
            const displayDate = entry.date || primaryReq?.date;
            const displayReason = entry.reason || primaryReq?.reason;
            const displayStatus = entry.status || primaryReq?.status;
            const displayCreatedAt =
              entry.createdAt || primaryReq?.createdAt || primaryReq?.requestDate;
            const isExpanded = expandedGroups[entry.id] ?? false;
            const visibleEmployees = isGroup && !isExpanded ? entry.items.slice(0, 8) : entry.items;
            const hiddenCount =
              isGroup && !isExpanded ? Math.max(entry.items.length - visibleEmployees.length, 0) : 0;

            return (
              <motion.div
                key={entry.id}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: Math.min(index * 0.05, 0.3) }}
              >
                <Card className="hover:shadow-lg transition-shadow duration-300">
                  <Card.Body className="p-6">
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center space-x-4 min-w-0">
                        {isGroup ? (
                          <div className="w-12 h-12 rounded-full bg-emerald-100 flex items-center justify-center flex-shrink-0">
                            <UsersIcon className="w-6 h-6 text-emerald-700" />
                          </div>
                        ) : (
                          (() => {
                            const { employeeEmail, employeeRecord, employeeName } =
                              resolveEmployeeFromRequest(primaryReq, employees);
                            return (
                              <EmployeeAvatar
                                record={employeeRecord || primaryReq}
                                name={employeeName}
                                email={employeeEmail}
                                sizeClass="w-12 h-12"
                              />
                            );
                          })()
                        )}
                        <div className="min-w-0">
                          <h3 className="text-lg font-semibold text-gray-900">
                            {isGroup
                              ? `WFH Group — ${entry.items.length} employees`
                              : resolveEmployeeFromRequest(primaryReq, employees).employeeName}
                          </h3>
                          <p className="text-sm text-gray-600">
                            {isGroup
                              ? 'Declared together for the same date and reason'
                              : primaryReq.email_address || primaryReq.employeeEmail}
                          </p>
                        </div>
                      </div>
                      <div className="flex flex-col items-end gap-2">
                        <span
                          className={`px-3 py-1 text-sm font-medium rounded-full ${getStatusColor(
                            displayStatus
                          )}`}
                        >
                          {(displayStatus || '').charAt(0).toUpperCase() +
                            (displayStatus || '').slice(1).toLowerCase()}
                        </span>
                        <div className="flex space-x-2">
                          {isGroup ? (
                            <>
                              <button
                                onClick={() => openEditBatch(entry)}
                                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Edit batch"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleBatchDelete(entry)}
                                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                title="Delete batch"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => openEdit(primaryReq)}
                                className="p-2 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Edit"
                              >
                                <PencilIcon className="w-4 h-4" />
                              </button>
                              <button
                                onClick={() => handleDelete(primaryReq.id)}
                                className="p-2 text-gray-400 hover:text-red-600 transition-colors"
                                title="Delete"
                              >
                                <TrashIcon className="w-4 h-4" />
                              </button>
                            </>
                          )}
                        </div>
                      </div>
                    </div>

                    {isGroup && (
                      <div className="mt-4">
                        <div className="rounded-xl border border-emerald-100 bg-emerald-50/60 p-4">
                          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
                            {visibleEmployees.map((req) => {
                              const { employeeEmail, employeeRecord, employeeName } =
                                resolveEmployeeFromRequest(req, employees);
                              return (
                                <div
                                  key={req.id}
                                  className="flex items-center gap-3 rounded-lg bg-white px-3 py-2 border border-emerald-100"
                                >
                                  <EmployeeAvatar
                                    record={employeeRecord || req}
                                    name={employeeName}
                                    email={employeeEmail}
                                    sizeClass="w-9 h-9"
                                  />
                                  <div className="min-w-0 flex-1">
                                    <p className="text-sm font-medium text-gray-900 truncate">
                                      {employeeName}
                                    </p>
                                    <p className="text-xs text-gray-500 truncate">{employeeEmail}</p>
                                  </div>
                                  <button
                                    onClick={() => handleDelete(req.id)}
                                    className="p-1.5 text-gray-400 hover:text-red-600 transition-colors flex-shrink-0"
                                    title="Remove from batch"
                                  >
                                    <TrashIcon className="w-3.5 h-3.5" />
                                  </button>
                                </div>
                              );
                            })}
                          </div>
                          {(hiddenCount > 0 || (isExpanded && entry.items.length > 8)) && (
                            <button
                              type="button"
                              onClick={() => toggleGroupExpanded(entry.id)}
                              className="mt-3 inline-flex items-center gap-1 text-sm font-medium text-emerald-700 hover:text-emerald-800"
                            >
                              {isExpanded ? (
                                <>
                                  <ChevronUpIcon className="w-4 h-4" />
                                  Show fewer employees
                                </>
                              ) : (
                                <>
                                  <ChevronDownIcon className="w-4 h-4" />
                                  Show {hiddenCount} more employee{hiddenCount > 1 ? 's' : ''}
                                </>
                              )}
                            </button>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-4">
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500">WFH Date</p>
                          <p className="font-medium text-sm">
                            {displayDate ? formatDateDisplay(displayDate) : '—'}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <CalendarIcon className="w-4 h-4 text-gray-400" />
                        <div>
                          <p className="text-xs text-gray-500">Requested On</p>
                          <p className="font-medium text-sm">
                            {displayCreatedAt
                              ? formatDateDisplay(displayCreatedAt)
                              : '—'}
                          </p>
                        </div>
                      </div>
                    </div>

                    {displayReason && (
                      <div className="mt-4">
                        <p className="text-xs text-gray-500">Reason</p>
                        <p className="text-gray-900">{displayReason}</p>
                      </div>
                    )}

                    {(displayStatus || '').toLowerCase() === 'pending' && (
                      <div className="mt-6 flex gap-3">
                        <Button
                          variant="success"
                          onClick={() =>
                            isGroup
                              ? handleGroupApproval(entry.items, 'approved')
                              : handleApproval(primaryReq.id, 'approved')
                          }
                          icon={CheckCircleIcon}
                        >
                          {isGroup ? `Approve all (${entry.items.length})` : 'Approve'}
                        </Button>
                        <Button
                          variant="danger"
                          onClick={() => {
                            setSelected(
                              isGroup
                                ? { groupItems: entry.items, date: primaryReq.date }
                                : primaryReq
                            );
                            setShowRejectModal(true);
                          }}
                          icon={XCircleIcon}
                        >
                          {isGroup ? `Reject all (${entry.items.length})` : 'Reject'}
                        </Button>
                      </div>
                    )}
                  </Card.Body>
                </Card>
              </motion.div>
            );
          })}
        </div>
      )}

      <ListPagination {...pagination} label="WFH entries" disabled={loading || submitting} />

      {/* Declare / Edit Modal */}
      <AnimatePresence>
        {showForm && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={closeForm}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className={`bg-white rounded-2xl shadow-xl w-full max-h-[90vh] overflow-y-auto flex flex-col ${
                editing && !editingBatch ? 'max-w-md' : 'max-w-lg'
              }`}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">
                  {editingBatch
                    ? 'Edit WFH Batch'
                    : editing
                    ? 'Edit WFH Request'
                    : 'Declare WFH for Employee(s)'}
                </h2>
                <button
                  onClick={closeForm}
                  className="p-1 rounded-lg text-gray-400 hover:bg-gray-100 hover:text-gray-600"
                >
                  <XMarkIcon className="w-5 h-5" />
                </button>
              </div>
              <form onSubmit={handleSubmit} className="p-6 space-y-4">
                <div>
                  <div className="flex items-center justify-between mb-1">
                    <label className="block text-sm font-medium text-gray-700">
                      {editing && !editingBatch ? 'Employee' : 'Employees'}{' '}
                      <span className="text-red-500">*</span>
                    </label>
                    {!editing && !editingBatch && employeeList.length > 0 && (
                      <label className="flex items-center gap-2 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={allEmployeesSelected}
                          onChange={toggleSelectAllEmployees}
                          className="h-4 w-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                        />
                        <span className="text-sm font-medium text-gray-700">Select all</span>
                      </label>
                    )}
                  </div>
                  {editing && !editingBatch ? (
                    <Dropdown
                      value={form.employeeEmail}
                      onChange={(value) =>
                        setForm((p) => ({ ...p, employeeEmail: value }))
                      }
                      options={employeeOptions}
                      placeholder="Select employee"
                      searchable
                      className="w-full"
                    />
                  ) : editingBatch || !allEmployeesSelected ? (
                    <>
                      <MultiSelect
                        value={form.employeeEmails}
                        onChange={(value) =>
                          setForm((p) => ({ ...p, employeeEmails: value }))
                        }
                        options={employeeOptions}
                        placeholder="Select employee(s)"
                        className="w-full"
                      />
                      {form.employeeEmails.length > 0 && (
                        <p className="text-xs text-gray-500 mt-1">
                          {form.employeeEmails.length} of {employeeList.length} selected
                        </p>
                      )}
                    </>
                  ) : allEmployeesSelected ? (
                    <div className="flex items-center justify-between gap-3 w-full min-h-[44px] px-4 py-2 border border-emerald-200 rounded-lg bg-emerald-50">
                      <div className="flex items-center gap-2 min-w-0">
                        <UsersIcon className="w-5 h-5 text-emerald-700 flex-shrink-0" />
                        <span className="text-sm font-medium text-emerald-900">
                          All employees ({employeeList.length})
                        </span>
                      </div>
                      <button
                        type="button"
                        onClick={toggleSelectAllEmployees}
                        className="text-sm font-medium text-emerald-700 hover:text-emerald-900 flex-shrink-0"
                      >
                        Clear
                      </button>
                    </div>
                  ) : null}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WFH Date <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="date"
                    value={form.date}
                    onChange={(e) => setForm((p) => ({ ...p, date: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.reason}
                    onChange={(e) => setForm((p) => ({ ...p, reason: e.target.value }))}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    placeholder="Reason for WFH"
                    required
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Description (Optional)
                  </label>
                  <textarea
                    value={form.description}
                    onChange={(e) =>
                      setForm((p) => ({ ...p, description: e.target.value }))
                    }
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500"
                    rows="3"
                    placeholder="Additional details..."
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button type="submit" className="flex-1" disabled={submitting}>
                    {submitting
                      ? 'Saving...'
                      : editingBatch
                      ? 'Update WFH Batch'
                      : editing
                      ? 'Update WFH'
                      : form.employeeEmails.length > 1
                      ? `Declare WFH (${form.employeeEmails.length})`
                      : 'Declare WFH'}
                  </Button>
                  <Button
                    type="button"
                    variant="outline"
                    onClick={closeForm}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </form>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Reject Modal */}
      <AnimatePresence>
        {showRejectModal && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4"
            onClick={() => {
              setShowRejectModal(false);
              setRejectionReason('');
              setSelected(null);
            }}
          >
            <motion.div
              initial={{ scale: 0.95, opacity: 0, y: 12 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.95, opacity: 0 }}
              transition={{ type: 'spring', damping: 26, stiffness: 320 }}
              className="bg-white rounded-2xl shadow-xl w-full max-w-md overflow-hidden"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="px-6 py-4 border-b border-gray-100">
                <h2 className="text-lg font-bold text-gray-900">Reject WFH Request</h2>
              </div>
              <div className="p-6 space-y-4">
                <div>
                  {selected?.groupItems?.length ? (
                    <>
                      <p className="text-sm text-gray-600 mb-1">
                        Rejecting WFH group for {selected.groupItems.length} employees:
                      </p>
                      <p className="text-sm text-gray-600">
                        Date:{' '}
                        {selected?.date ? formatDateDisplay(selected.date) : '—'}
                      </p>
                    </>
                  ) : (
                    <>
                      <p className="text-sm text-gray-600 mb-1">Rejecting request from:</p>
                      <p className="font-medium text-gray-900">
                        {selected?.employee
                          ? `${selected.employee.first_name} ${selected.employee.last_name}`
                          : selected?.email_address}
                      </p>
                      <p className="text-sm text-gray-600">
                        Date:{' '}
                        {selected?.date ? formatDateDisplay(selected.date) : '—'}
                      </p>
                    </>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Reason for Rejection <span className="text-red-500">*</span>
                  </label>
                  <textarea
                    value={rejectionReason}
                    onChange={(e) => setRejectionReason(e.target.value)}
                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-red-500 focus:border-red-500"
                    rows="4"
                    placeholder="Please provide a reason..."
                    required
                  />
                </div>
                <div className="flex gap-3 pt-2">
                  <Button
                    variant="danger"
                    onClick={confirmReject}
                    disabled={!rejectionReason.trim()}
                    className="flex-1"
                  >
                    Confirm Rejection
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => {
                      setShowRejectModal(false);
                      setRejectionReason('');
                      setSelected(null);
                    }}
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default WFHManagement;
